import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, ArrowRight, ArrowLeft, Loader2, CheckCircle2, Sparkles, RotateCcw, Plus, Check, Bell, X, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DogPicker } from '../components/DogPicker';
import { useBox } from '../context/BoxContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DID_YOU_KNOW = [
  'The first ingredient on the label tells you more about quality than any marketing on the front.',
  '"Meat meal" is a dried, processed ingredient — not the same as freshly prepared meat.',
  'Dogs digest animal protein more efficiently than plant-based protein sources.',
  'Some dog foods boost protein levels using peas or plant proteins instead of meat.',
  'Gently cooked food helps preserve nutrients and improves digestibility.',
  'Ingredients are listed by weight before cooking — which can change their real contribution.',
  'Added prebiotics like MOS & FOS help support your dog\'s digestive health.',
  'Grain-free doesn\'t always mean better — it depends on what replaces the grains.',
  'Many common issues like itching or loose stools can be linked to ingredient quality.',
  'Dogs often thrive on diets with higher quality, more digestible protein sources.',
];

const DidYouKnow = ({ active }) => {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * DID_YOU_KNOW.length));

  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      setIndex(prev => {
        let next;
        do { next = Math.floor(Math.random() * DID_YOU_KNOW.length); } while (next === prev);
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [active]);

  if (!active) return null;

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4 }}
      className="max-w-md mx-auto mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center"
      data-testid="did-you-know"
    >
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1.5">Did you know?</p>
      <p className="text-sm text-amber-900 leading-relaxed">{DID_YOU_KNOW[index]}</p>
    </motion.div>
  );
};

function formatApiError(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join('. ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const STEPS = ['upload', 'review', 'results'];

const ANALYZE_STATUS_MESSAGES = [
  { after: 0,  text: 'Ted is reading your bag…' },
  { after: 4,  text: 'Ted is checking the back for ingredients…' },
  { after: 9,  text: 'Ted is working harder to read the small print…' },
  { after: 15, text: 'Almost there — decoding the nutrition panel…' },
  { after: 22, text: 'Ted is still on it — a sharp, well-lit image helps next time.' },
];

const AnalyzeStatus = ({ active }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;

  const current = [...ANALYZE_STATUS_MESSAGES].reverse().find(m => elapsed >= m.after) || ANALYZE_STATUS_MESSAGES[0];
  const working = elapsed >= 9;

  return (
    <motion.div
      key={current.text}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto mt-4 text-center"
      data-testid="analyze-status"
    >
      <p className={`text-sm font-semibold ${working ? 'text-[#0FA49C]' : 'text-[#666]'}`}>
        {current.text}
      </p>
      {working && (
        <p className="text-xs text-[#999] mt-1">This scan is taking extra care — just a moment.</p>
      )}
    </motion.div>
  );
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const ImageUploadBox = ({ label, image, preview, onUpload, testId }) => {
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please use JPEG, PNG, or WebP images');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10MB.');
      return;
    }
    const base64 = await fileToBase64(file);
    const previewUrl = URL.createObjectURL(file);
    onUpload(base64, previewUrl);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all overflow-hidden ${
        preview
          ? 'border-[#0FA49C] bg-[#0FA49C]/5'
          : 'border-gray-300 bg-gray-50 hover:border-[#0FA49C] hover:bg-[#0FA49C]/5'
      }`}
      style={{ aspectRatio: '3/4' }}
      data-testid={testId}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
      />
      {preview ? (
        <>
          <img src={preview} alt={label} className="w-full h-full object-cover" />
          <div className="absolute top-3 right-3 w-8 h-8 bg-[#0FA49C] rounded-full flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-3">
            <p className="text-white text-sm font-semibold">{label}</p>
            <p className="text-white/70 text-xs">Tap to change</p>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#0FA49C]/10 flex items-center justify-center mb-4">
            <Camera className="w-8 h-8 text-[#0FA49C]" />
          </div>
          <p className="font-semibold text-[#222] text-sm mb-1">{label}</p>
          <p className="text-xs text-[#999]">Tap to upload photo</p>
        </div>
      )}
    </div>
  );
};

export const TedAI = ({ onCompleteOrder }) => {
  const { addProduct, isProductSelected, isFull, selectedProducts, boxSize } = useBox();
  const { customer, login, register } = useAuth();
  const [step, setStep] = useState('upload');
  const [frontImage, setFrontImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [imageUrls, setImageUrls] = useState({ front: null, back: null });
  const [matching, setMatching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [showPriceAlert, setShowPriceAlert] = useState(false);
  const [priceAlertChecked, setPriceAlertChecked] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [bagWeight, setBagWeight] = useState('');
  const [bagWeightFromAI, setBagWeightFromAI] = useState(false);
  const [dogName, setDogName] = useState('');
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  // Auth form state for non-logged-in users
  const [authMode, setAuthMode] = useState('login');
  const [authName, setAuthName] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [showHowWeMatch, setShowHowWeMatch] = useState(false);
  const [showBoxFullPopup, setShowBoxFullPopup] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const extraFileInputRef = useRef(null);

  const TED_AI_FIELDS = [
    { key: 'brand', label: 'Brand', weight: 1 },
    { key: 'product_name', label: 'Product name', weight: 1 },
    { key: 'protein_source', label: 'Protein source', weight: 1 },
    { key: 'ingredients', label: 'Ingredients list', weight: 2 },
    { key: 'type', label: 'Recipe type', weight: 1 },
    { key: 'lifestage', label: 'Lifestage', weight: 1 },
    { key: 'breed_size', label: 'Breed size', weight: 1 },
    { key: 'special_needs', label: 'Special needs', weight: 1 },
    // Analytical constituents (protein %, fat %, fibre %, ash %) carry the most weight
    // because % values are critical for accurate matching.
    { key: 'analytical_constituents', label: 'Nutrition % (protein / fat / fibre)', weight: 4, critical: true },
    { key: 'bag_weight', label: 'Bag weight', weight: 1 },
  ];

  // When this field is missing, cap the completeness at this ceiling so it's
  // clearly flagged as an incomplete scan.
  const CRITICAL_MISSING_CAP = 70;

  const EMPTY_VALUES = [
    '', 'none', 'n/a', 'na', 'unknown', 'not visible', 'not specified',
    'not available', 'not provided', 'not listed', 'not shown', 'unspecified',
    'no info', 'no information', 'undefined', 'null',
  ];

  const isFieldFilled = (v) => {
    if (v == null) return false;
    if (typeof v === 'string') {
      const low = v.trim().toLowerCase();
      return !!low && !EMPTY_VALUES.includes(low);
    }
    return true;
  };

  const completeness = (() => {
    if (!analysis) return { pct: 0, missing: TED_AI_FIELDS, criticalMissing: [] };
    const totalWeight = TED_AI_FIELDS.reduce((s, f) => s + f.weight, 0);
    const filledWeight = TED_AI_FIELDS
      .filter(f => isFieldFilled(analysis[f.key]))
      .reduce((s, f) => s + f.weight, 0);
    let pct = Math.round((filledWeight / totalWeight) * 100);
    const missing = TED_AI_FIELDS.filter(f => !isFieldFilled(analysis[f.key]));
    const criticalMissing = missing.filter(f => f.critical);
    if (criticalMissing.length > 0) {
      pct = Math.min(pct, CRITICAL_MISSING_CAP);
    }
    return { pct, missing, criticalMissing };
  })();

  const handleExtraImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Please use one under 10MB.');
      e.target.value = '';
      return;
    }
    try {
      setUploadingExtra(true);
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const hint = completeness.missing.slice(0, 3).map(f => f.label).join(', ');
      const res = await axios.post(`${API}/ted-ai/analyze-extra`, {
        current_analysis: analysis,
        image_base64: b64,
        hint: hint || null,
      });
      if (res.data.status === 'success') {
        setAnalysis(res.data.analysis);
        if (!bagWeight && res.data.analysis.bag_weight) {
          setBagWeight(res.data.analysis.bag_weight);
          setBagWeightFromAI(true);
        }
        const filled = res.data.filled_fields || [];
        if (filled.length) {
          toast.success(`Ted filled in: ${filled.join(', ')}`);
        } else {
          toast('No new details found in that image — try a clearer close-up.');
        }
      } else {
        toast.error(res.data.message || 'Could not read that image');
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Extra image failed');
    } finally {
      setUploadingExtra(false);
      e.target.value = '';
    }
  };

  const handleAnalyze = async () => {
    if (!frontImage || !backImage) {
      toast.error('Please upload both front and back images');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ted-ai/analyze`, {
        front_image: frontImage,
        back_image: backImage,
      });

      if (response.data.status === 'success') {
        setAnalysis(response.data.analysis);
        setBagWeight(response.data.analysis.bag_weight || '');
        setBagWeightFromAI(!!response.data.analysis.bag_weight);
        setImageUrls({
          front: response.data.front_image_url,
          back: response.data.back_image_url,
        });
        setStep('review');
      } else {
        toast.error(response.data.message || 'Could not analyze images');
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMatch = async (email) => {
    if (!analysis) return;

    setMatching(true);
    try {
      const response = await axios.post(`${API}/ted-ai/match`, {
        brand: String(analysis.brand || ''),
        product_name: String(analysis.product_name || ''),
        protein_source: analysis.protein_source ? String(analysis.protein_source) : null,
        ingredients: analysis.ingredients ? String(analysis.ingredients) : null,
        type: analysis.type ? String(analysis.type) : null,
        lifestage: analysis.lifestage ? String(analysis.lifestage) : null,
        breed_size: analysis.breed_size ? String(analysis.breed_size) : null,
        special_needs: analysis.special_needs ? String(analysis.special_needs) : null,
        bag_weight: bagWeight || null,
        analytical_constituents: analysis.analytical_constituents ? String(analysis.analytical_constituents) : null,
        nutrients: analysis.nutrients && typeof analysis.nutrients === 'object' ? analysis.nutrients : null,
        nutritional_additives: analysis.nutritional_additives ? String(analysis.nutritional_additives) : null,
        front_image_url: imageUrls.front,
        back_image_url: imageUrls.back,
        email: email,
        dog_name: dogName.trim() || null,
      });

      if (response.data.status === 'success') {
        setMatches(response.data.matches);
        setExplanation(response.data.explanation);
        setStep('results');
      } else {
        toast.error(response.data.message || 'Could not find matches');
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Matching failed. Please try again.');
    } finally {
      setMatching(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFrontImage(null);
    setFrontPreview(null);
    setBackImage(null);
    setBackPreview(null);
    setAnalysis(null);
    setImageUrls({ front: null, back: null });
    setMatches(null);
    setExplanation('');
    setUserEmail('');
    setCurrentPrice('');
    setBagWeight('');
    setBagWeightFromAI(false);
    setDogName('');
    setPriceAlertChecked(false);
    setShowPriceAlert(false);
  };

  const handleAddToBox = (product) => {
    if (!isFull && !isProductSelected(product.id)) {
      addProduct(product);
      toast.success(`${product.name} added to your box!`);
      // Check if box is now full (selectedProducts + 1 because state hasn't updated yet)
      if (selectedProducts.length + 1 >= boxSize) {
        setTimeout(() => setShowBoxFullPopup(true), 500);
      }
    }
  };

  const handleEmailPopupSubmit = async (e) => {
    e.preventDefault();
    const email = customer ? customer.email : userEmail.trim();

    if (!customer) {
      // Non-logged-in: must enter email
      if (!email) {
        toast.error('Please enter your email address');
        return;
      }
    }

    // Save price alert if checked
    if (priceAlertChecked && currentPrice) {
      try {
        const parsedPrice = parseFloat(currentPrice);
        await axios.post(`${API}/ted-ai/price-alert`, {
          email: email,
          brand: analysis?.brand || '',
          product_name: analysis?.product_name || '',
          current_price: isNaN(parsedPrice) ? null : parsedPrice,
          bag_weight: bagWeight || null,
          dog_name: dogName.trim() || null,
        });
      } catch (error) {
        console.error('Price alert save failed:', error);
      }
    }

    setShowEmailPopup(false);
    handleMatch(email);
  };

  const handleFindMatchesClick = () => {
    if (customer) {
      // Logged in — show popup only for price alert option (no email needed)
      setShowEmailPopup(true);
    } else {
      // Not logged in — show popup with email/auth
      setShowEmailPopup(true);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSubmitting(true);
    try {
      if (authMode === 'register') {
        if (!authName.trim()) { setAuthError('Please enter your name'); setAuthSubmitting(false); return; }
        await register(authName.trim(), userEmail.trim(), authPassword);
      } else {
        await login(userEmail.trim(), authPassword);
      }
      toast.success(authMode === 'register' ? 'Account created!' : 'Signed in!');
      // After auth, proceed with matching using the email
      setShowEmailPopup(false);
      handleMatch(userEmail.trim());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAuthError(formatApiError(detail) || err.message);
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleGoogleLoginFromPopup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-0">
      {/* Hero Band — rustic teal, compact, two-column */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-none sm:rounded-3xl mt-0 sm:mt-6 mb-8 -mx-4 sm:mx-0 px-6 sm:px-10 py-6 sm:py-8"
        style={{
          backgroundColor: '#1f6d62',
          backgroundImage: `
            radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.12) 0%, transparent 45%),
            radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%),
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")
          `,
        }}
        data-testid="ted-ai-hero"
      >
        <svg className="absolute -top-6 -left-10 w-52 h-16 opacity-20 pointer-events-none" viewBox="0 0 300 80" fill="none">
          <path d="M0 40 Q 75 10, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <svg className="absolute bottom-2 right-4 w-52 h-16 opacity-15 pointer-events-none" viewBox="0 0 300 80" fill="none">
          <path d="M0 40 Q 75 70, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
        </svg>

        <div className="relative flex items-center gap-4 sm:gap-6">
          {/* Text — left */}
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-2">
              <Sparkles className="w-3 h-3" /> AI Food Matcher
            </span>
            <h2 className="text-white text-2xl sm:text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Meet <span className="text-[#a0da39]">Ted AI</span>
            </h2>
            <p
              className="text-[#a0da39] mt-1 text-lg sm:text-xl md:text-2xl italic leading-tight"
              style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
            >
              Find your dog's perfect match.
            </p>
            <p className="text-white/85 mt-2 text-xs sm:text-sm leading-relaxed">
              Upload a photo of your current bag — Ted finds the best match from our range.
            </p>
          </div>

          {/* Ted — right */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-[#a0da39]/30 blur-xl rounded-full" />
            <img
              src="https://static.prod-images.emergentagent.com/jobs/17e0cf3f-151a-4992-836a-2a69f9e5ca7d/images/d42ed1b3257157a64da925367ecbfdcc24f9bf772ddf461c2701a71cf90d9e59.png"
              alt="Ted the sausage dog"
              className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full object-cover border-4 border-[#a0da39] shadow-lg"
            />
          </div>
        </div>
      </motion.div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-8" data-testid="ted-ai-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <div className={`w-8 h-0.5 ${STEPS.indexOf(step) >= i ? 'bg-[#0FA49C]' : 'bg-gray-200'}`} />}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              step === s ? 'bg-[#0FA49C] text-white' :
              STEPS.indexOf(step) > i ? 'bg-[#0FA49C]/20 text-[#0FA49C]' :
              'bg-gray-200 text-[#999]'
            }`}>
              {STEPS.indexOf(step) > i ? <Check className="w-4 h-4" /> : i + 1}
            </div>
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1: Upload */}
        {step === 'upload' && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-2">
              <h3 className="font-bold text-[#222] text-lg">Upload your dog food bag</h3>
              <p className="text-sm text-[#666]">Take a clear photo of the front and back of the bag</p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <ImageUploadBox
                label="Front of Bag"
                image={frontImage}
                preview={frontPreview}
                onUpload={(b64, preview) => { setFrontImage(b64); setFrontPreview(preview); }}
                testId="upload-front"
              />
              <ImageUploadBox
                label="Back of Bag"
                image={backImage}
                preview={backPreview}
                onUpload={(b64, preview) => { setBackImage(b64); setBackPreview(preview); }}
                testId="upload-back"
              />
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleAnalyze}
                disabled={!frontImage || !backImage || analyzing}
                className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl px-8 py-6 text-lg font-bold"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Ted is reading your bag...
                  </>
                ) : (
                  <>
                    Analyze with Ted AI
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <DidYouKnow active={analyzing} />
            <AnalyzeStatus active={analyzing} />
          </motion.div>
        )}

        {/* STEP 2: Review Analysis */}
        {step === 'review' && analysis && (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-2">
              <h3 className="font-bold text-[#222] text-lg">Ted found this information</h3>
              <p className="text-sm text-[#666]">Please confirm these details are correct</p>
            </div>

            {/* Completeness card */}
            <div
              className={`rounded-2xl border-2 p-4 ${
                completeness.pct >= 90
                  ? 'bg-green-50 border-green-200'
                  : completeness.pct >= 60
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200'
              }`}
              data-testid="completeness-card"
            >
              <div className="flex items-center gap-4">
                {/* Ring */}
                <div className="relative w-16 h-16 flex-shrink-0">
                  <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" strokeWidth="6" className="stroke-gray-200 fill-none" />
                    <circle
                      cx="32" cy="32" r="28" strokeWidth="6" strokeLinecap="round"
                      className={`fill-none ${
                        completeness.pct >= 90 ? 'stroke-green-500' :
                        completeness.pct >= 60 ? 'stroke-amber-500' : 'stroke-red-500'
                      }`}
                      strokeDasharray={2 * Math.PI * 28}
                      strokeDashoffset={2 * Math.PI * 28 * (1 - completeness.pct / 100)}
                      style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-extrabold text-[#222]" data-testid="completeness-pct">
                      {completeness.pct}%
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#222] text-sm">
                    {completeness.criticalMissing.length > 0
                      ? "Ted needs the nutrition % panel"
                      : completeness.pct >= 90
                        ? "Ted's got everything he needs"
                        : completeness.pct >= 60
                          ? 'Pretty good — a little more would help'
                          : 'Ted needs more details'}
                  </p>
                  {completeness.criticalMissing.length > 0 ? (
                    <p className="text-xs text-[#666] mt-0.5">
                      <strong className="text-red-700">Missing:</strong> protein % / fat % / fibre % / ash %.
                      Accuracy is capped at {CRITICAL_MISSING_CAP}% until Ted can read the nutrition panel.
                    </p>
                  ) : completeness.missing.length > 0 ? (
                    <p className="text-xs text-[#666] mt-0.5">
                      Missing: <span className="font-semibold">{completeness.missing.slice(0, 3).map(f => f.label).join(', ')}</span>
                      {completeness.missing.length > 3 && ` + ${completeness.missing.length - 3} more`}
                    </p>
                  ) : (
                    <p className="text-xs text-[#666] mt-0.5">All key fields detected.</p>
                  )}
                </div>
              </div>

              {completeness.missing.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/40">
                  <p className="text-xs text-[#444] mb-2">
                    {completeness.criticalMissing.length > 0 ? (
                      <>Snap a close-up of the <strong>nutrition panel</strong> (protein %, fat %, fibre %, ash %) — this unlocks accurate matching.</>
                    ) : (
                      <>Snap a close-up of the <strong>ingredients panel</strong> or <strong>nutrition label</strong> to improve accuracy.</>
                    )}
                  </p>
                  <input
                    ref={extraFileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleExtraImage}
                    data-testid="extra-image-input"
                  />
                  <Button
                    type="button"
                    onClick={() => extraFileInputRef.current?.click()}
                    disabled={uploadingExtra}
                    className="w-full rounded-xl bg-[#0FA49C] hover:bg-[#0d8f88] text-white py-3 font-semibold"
                    data-testid="extra-image-btn"
                  >
                    {uploadingExtra ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading image…</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" />
                        {completeness.criticalMissing.length > 0 ? 'Add nutrition panel photo' : 'Add an extra photo'}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="analysis-result">
              {/* Brand & Product header */}
              <div className="bg-gradient-to-r from-[#0FA49C] to-[#0d8f88] p-6 text-white relative">
                <p className="text-sm font-medium text-white/80 mb-1">Current Food</p>
                <h4 className="text-xl font-bold">{analysis.brand}</h4>
                <p className="text-white/90">{analysis.product_name}</p>
                {analysis.confidence && (
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                    analysis.confidence === 'high' ? 'bg-green-500/30 text-green-100' :
                    analysis.confidence === 'medium' ? 'bg-yellow-500/30 text-yellow-100' :
                    'bg-red-500/30 text-red-100'
                  }`}>
                    {analysis.confidence} confidence
                  </span>
                )}
              </div>

              {/* Details grid */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {isFieldFilled(analysis.protein_source) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-[#999] mb-0.5">Protein</p>
                      <p className="font-semibold text-[#222] text-sm">{analysis.protein_source}</p>
                    </div>
                  )}
                  {isFieldFilled(analysis.type) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-[#999] mb-0.5">Type</p>
                      <p className="font-semibold text-[#222] text-sm capitalize">{analysis.type.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                  {isFieldFilled(analysis.lifestage) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-[#999] mb-0.5">Lifestage</p>
                      <p className="font-semibold text-[#222] text-sm capitalize">{analysis.lifestage}</p>
                    </div>
                  )}
                  {isFieldFilled(analysis.breed_size) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-[#999] mb-0.5">Breed Size</p>
                      <p className="font-semibold text-[#222] text-sm capitalize">{analysis.breed_size}</p>
                    </div>
                  )}
                  {isFieldFilled(analysis.special_needs) && (
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                      <p className="text-xs text-[#999] mb-0.5">Special Needs</p>
                      <p className="font-semibold text-[#222] text-sm">{analysis.special_needs}</p>
                    </div>
                  )}
                  {isFieldFilled(bagWeight) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-[#999] mb-0.5">Bag Weight</p>
                      <p className="font-semibold text-[#222] text-sm">{bagWeight}</p>
                    </div>
                  )}
                </div>

                {isFieldFilled(analysis.ingredients) && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-[#999] mb-1">Key Ingredients</p>
                    <p className="text-sm text-[#444] leading-relaxed">{analysis.ingredients}</p>
                  </div>
                )}

                {isFieldFilled(analysis.analytical_constituents) && (
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-[#999] mb-1">Analytical Constituents</p>
                    <p className="text-sm text-[#444]">{analysis.analytical_constituents}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-xl px-6 py-5"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={handleFindMatchesClick}
                disabled={matching}
                className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl px-8 py-5 text-base font-bold"
                data-testid="find-matches-btn"
              >
                {matching ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Finding matches...
                  </>
                ) : (
                  <>
                    Find My Matches
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <DidYouKnow active={matching} />
          </motion.div>
        )}

        {/* STEP 3: Results */}
        {step === 'results' && matches && (
          <motion.div
            key="results"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-2">
              <h3 className="font-bold text-[#222] text-lg">Your Perfect Matches</h3>
              {explanation && (
                <p className="text-sm text-[#666] max-w-lg mx-auto mt-2">{explanation}</p>
              )}
            </div>

            <div className="space-y-4" data-testid="match-results">
              {matches.map((match, i) => {
                const product = match.product;
                const selected = isProductSelected(product.id);
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className={`bg-white rounded-2xl border overflow-hidden ${
                      selected ? 'border-[#0FA49C] ring-2 ring-[#0FA49C]/20' : 'border-gray-200'
                    }`}
                    data-testid={`match-card-${product.id}`}
                  >
                    <div className="flex flex-col sm:flex-row">
                      {/* Product image */}
                      <div className="sm:w-40 h-40 sm:h-auto bg-gray-50 flex-shrink-0 relative">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Match score badge */}
                        <div className="absolute top-3 left-3 bg-[#0FA49C] text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg">
                          {match.match_score}% match
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4 sm:p-5 flex flex-col">
                        <div className="flex-1">
                          <h4 className="font-bold text-[#222] text-base mb-3">{product.name}</h4>

                          {/* Benefit Icons */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {product.type === 'grain_free' && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                                <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                                <span className="text-[11px] font-semibold text-emerald-700">Grain Free</span>
                              </div>
                            )}
                            {product.type === 'hydrolysed' && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-violet-50 border border-violet-200">
                                <svg className="w-4 h-4 text-violet-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>
                                <span className="text-[11px] font-semibold text-violet-700">Hydrolysed</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0FA49C]/5 border border-[#0FA49C]/20">
                              <svg className="w-4 h-4 text-[#0FA49C]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/></svg>
                              <span className="text-[11px] font-semibold text-[#0FA49C]">Freshly Prepared</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                              <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                              <span className="text-[11px] font-semibold text-blue-700">Added Prebiotics</span>
                            </div>
                            {(product.tags || []).includes('skin_coat') && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                                <svg className="w-4 h-4 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
                                <span className="text-[11px] font-semibold text-amber-700">Skin & Coat</span>
                              </div>
                            )}
                            {(product.tags || []).includes('joint_care_pack') && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-50 border border-orange-200">
                                <svg className="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"/><path d="M2 20h20"/><path d="M14 12H10"/></svg>
                                <span className="text-[11px] font-semibold text-orange-700">Joint Support</span>
                              </div>
                            )}
                            {(product.tags || []).includes('fish_based') && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cyan-50 border border-cyan-200">
                                <svg className="w-4 h-4 text-cyan-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.46-3.44 6-7 6-3.56 0-7.56-2.54-8.5-6Z"/><path d="M2 12s1-2 3-2 3 2 3 2"/></svg>
                                <span className="text-[11px] font-semibold text-cyan-700">Omega-3 Rich</span>
                              </div>
                            )}
                            {(product.tags || []).includes('digestive_support') && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-lime-50 border border-lime-200">
                                <svg className="w-4 h-4 text-lime-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                <span className="text-[11px] font-semibold text-lime-700">Digestive Care</span>
                              </div>
                            )}
                          </div>

                          {/* Why Selected */}
                          {match.why_selected && (
                            <div className="bg-[#0FA49C]/5 border border-[#0FA49C]/20 rounded-xl p-3 mb-3">
                              <p className="text-xs font-bold text-[#0FA49C] uppercase tracking-wide mb-1">Why We Picked This</p>
                              <p className="text-sm text-[#444] leading-relaxed">{match.why_selected}</p>
                            </div>
                          )}

                          {/* How It's Better */}
                          {match.how_its_better && (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3">
                              <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">How It's Better</p>
                              <p className="text-sm text-[#444] leading-relaxed">{match.how_its_better}</p>
                            </div>
                          )}

                          {/* Why You Should Upgrade */}
                          {match.why_upgrade && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Why You Should Upgrade</p>
                              <p className="text-sm text-[#444] leading-relaxed">{match.why_upgrade}</p>
                            </div>
                          )}

                          {/* Fallback to reason for backwards compat */}
                          {!match.why_selected && match.reason && (
                            <p className="text-sm text-[#666] mb-3 leading-relaxed">{match.reason}</p>
                          )}

                          <div className="flex flex-wrap gap-1.5 mb-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#0FA49C]/10 text-[#0FA49C]">
                              {product.type.replace(/_/g, ' ')}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                              {product.lifestage}
                            </span>
                            {product.breed_size !== 'all' && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 capitalize">
                                {product.breed_size} breed
                              </span>
                            )}
                          </div>
                        </div>

                        <Button
                          onClick={() => handleAddToBox(product)}
                          disabled={selected || isFull}
                          className={`w-full sm:w-auto rounded-xl font-semibold ${
                            selected
                              ? 'bg-[#222] text-white'
                              : 'bg-[#0FA49C] text-white hover:bg-[#0d8f88]'
                          }`}
                          data-testid={`add-match-${product.id}`}
                        >
                          {selected ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Added to Box
                            </>
                          ) : isFull ? (
                            'Box Full'
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Add to Sample Box
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Complete Your Order Prompt */}
            {selectedProducts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={`rounded-2xl p-5 text-center ${
                  isFull
                    ? 'bg-gradient-to-r from-[#0FA49C] to-[#0d8f88] text-white'
                    : 'bg-[#0FA49C]/5 border-2 border-[#0FA49C]/20'
                }`}
                data-testid="complete-order-prompt"
              >
                {isFull ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-white" />
                    <h4 className="font-bold text-lg mb-1">Your box is full!</h4>
                    <p className="text-sm text-white/90 mb-4">
                      You've selected {selectedProducts.length} of {boxSize} samples. Ready to complete your order?
                    </p>
                    <Button
                      onClick={onCompleteOrder}
                      className="bg-white text-[#0FA49C] hover:bg-white/90 rounded-xl px-8 py-5 font-bold text-base"
                      data-testid="complete-order-btn"
                    >
                      Complete Your Order
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-[#222] mb-1">
                      {selectedProducts.length} of {boxSize} samples added
                    </p>
                    <p className="text-sm text-[#666]">
                      Add {boxSize - selectedProducts.length} more sample{boxSize - selectedProducts.length !== 1 ? 's' : ''} to complete your box, or browse more on the Sample Box tab.
                    </p>
                  </>
                )}
              </motion.div>
            )}

            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-xl px-6 py-5"
                data-testid="try-another-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Analyze Another Bag
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email/Auth Popup (before matching) */}
      <AnimatePresence>
        {showEmailPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEmailPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="email-popup"
            >
              <div className="bg-[#0FA49C] p-5 text-white flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base">
                      {customer ? 'Find Your Matches' : 'Almost there!'}
                    </h3>
                    <p className="text-xs text-white/80">
                      {customer
                        ? `Searching as ${customer.email}`
                        : 'Sign in or enter your email to continue'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowEmailPopup(false)} className="p-1 hover:bg-white/20 rounded-lg" data-testid="close-email-popup">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {customer ? (
                /* === LOGGED IN: just price alert + go === */
                <form onSubmit={handleEmailPopupSubmit} className="p-5 space-y-4">
                  {/* Which dog is this for? */}
                  <DogPicker
                    value={dogName}
                    onChange={setDogName}
                    label="Which dog is this for?"
                    placeholder="e.g. Buddy"
                    testId="ted-ai-dog-name"
                  />

                  {/* Price alert checkbox */}
                  <div
                    className={`rounded-xl border-2 transition-colors cursor-pointer ${
                      priceAlertChecked ? 'border-[#0FA49C] bg-[#0FA49C]/5' : 'border-gray-200 bg-gray-50'
                    }`}
                    onClick={() => setPriceAlertChecked(!priceAlertChecked)}
                    data-testid="price-alert-checkbox"
                  >
                    <div className="flex items-center gap-3 p-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        priceAlertChecked ? 'bg-[#0FA49C] border-[#0FA49C]' : 'border-gray-300'
                      }`}>
                        {priceAlertChecked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-[#0FA49C]" />
                        <span className="text-sm font-medium text-[#333]">Notify me if you find my food for less</span>
                      </div>
                    </div>
                    <AnimatePresence>
                      {priceAlertChecked && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 pb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div>
                              <label className="block text-xs text-[#666] mb-1">What do you currently pay?</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">&pound;</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPrice}
                                  onChange={(e) => setCurrentPrice(e.target.value)}
                                  placeholder="e.g. 45.99"
                                  className="pl-8"
                                  data-testid="price-alert-price-input"
                                />
                              </div>
                            </div>
                            {!bagWeightFromAI && (
                              <div>
                                <label className="block text-xs text-[#666] mb-1">Bag size/weight <span className="text-[#bbb]">(optional)</span></label>
                                <Input
                                  value={bagWeight}
                                  onChange={(e) => setBagWeight(e.target.value)}
                                  placeholder="e.g. 8kg, 2kg, 12kg"
                                  data-testid="bag-weight-input"
                                />
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <Button
                    type="submit"
                    disabled={matching}
                    className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold"
                    data-testid="submit-email-popup-btn"
                  >
                    {matching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Finding matches...
                      </>
                    ) : (
                      <>
                        Find My Matches
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                /* === NOT LOGGED IN: email + auth + price alert === */
                <div className="p-5 space-y-4">
                  {/* Google login */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleLoginFromPopup}
                    className="w-full py-5 font-semibold text-sm border-gray-300 hover:bg-gray-50"
                    data-testid="popup-google-login-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </Button>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-[#999] font-medium">or {authMode === 'login' ? 'sign in' : 'create account'}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  <form onSubmit={handleAuthSubmit} className="space-y-3">
                    {authMode === 'register' && (
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={authName}
                          onChange={(e) => setAuthName(e.target.value)}
                          placeholder="Your name"
                          className="pl-10"
                          data-testid="popup-auth-name"
                        />
                      </div>
                    )}
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="Email address"
                        required
                        className="pl-10"
                        data-testid="email-popup-input"
                      />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="password"
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                        placeholder="Password"
                        required
                        minLength={6}
                        className="pl-10"
                        data-testid="popup-auth-password"
                      />
                    </div>

                    {/* Price alert checkbox */}
                    <div
                      className={`rounded-xl border-2 transition-colors cursor-pointer ${
                        priceAlertChecked ? 'border-[#0FA49C] bg-[#0FA49C]/5' : 'border-gray-200 bg-gray-50'
                      }`}
                      onClick={() => setPriceAlertChecked(!priceAlertChecked)}
                      data-testid="price-alert-checkbox"
                    >
                      <div className="flex items-center gap-3 p-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          priceAlertChecked ? 'bg-[#0FA49C] border-[#0FA49C]' : 'border-gray-300'
                        }`}>
                          {priceAlertChecked && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-[#0FA49C]" />
                          <span className="text-sm font-medium text-[#333]">Notify me if you find my food for less</span>
                        </div>
                      </div>
                      <AnimatePresence>
                        {priceAlertChecked && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-3 pb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                              <div>
                                <label className="block text-xs text-[#666] mb-1">What do you currently pay?</label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-sm">&pound;</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={currentPrice}
                                    onChange={(e) => setCurrentPrice(e.target.value)}
                                    placeholder="e.g. 45.99"
                                    className="pl-8"
                                    data-testid="price-alert-price-input"
                                  />
                                </div>
                              </div>
                              {!bagWeightFromAI && (
                                <div>
                                  <label className="block text-xs text-[#666] mb-1">Bag size/weight <span className="text-[#bbb]">(optional)</span></label>
                                  <Input
                                    value={bagWeight}
                                    onChange={(e) => setBagWeight(e.target.value)}
                                    placeholder="e.g. 8kg, 2kg, 12kg"
                                    data-testid="bag-weight-input"
                                  />
                                </div>
                              )}
                              <div>
                                <label className="block text-xs text-[#666] mb-1">Dog's name <span className="text-[#bbb]">(optional)</span></label>
                                <Input
                                  value={dogName}
                                  onChange={(e) => setDogName(e.target.value)}
                                  placeholder="e.g. Buddy"
                                  data-testid="dog-name-input"
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {authError && (
                      <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{authError}</p>
                    )}

                    <Button
                      type="submit"
                      disabled={authSubmitting || matching}
                      className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold"
                      data-testid="submit-email-popup-btn"
                    >
                      {authSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : matching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Finding matches...
                        </>
                      ) : (
                        <>
                          {authMode === 'login' ? 'Sign In & Find Matches' : 'Create Account & Find Matches'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-center text-xs text-[#666]">
                    {authMode === 'login' ? (
                      <>No account? <button onClick={() => { setAuthMode('register'); setAuthError(''); }} className="text-[#0FA49C] font-semibold hover:underline">Sign up</button></>
                    ) : (
                      <>Have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); }} className="text-[#0FA49C] font-semibold hover:underline">Sign in</button></>
                    )}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Box Full Popup */}
      <AnimatePresence>
        {showBoxFullPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBoxFullPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="box-full-popup"
            >
              <div className="bg-gradient-to-r from-[#0FA49C] to-[#0d8f88] p-6 text-white">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
                <h3 className="font-bold text-xl">Your Box is Full!</h3>
                <p className="text-sm text-white/80 mt-1">{boxSize} samples selected and ready to go</p>
              </div>
              <div className="p-5 space-y-3">
                <Button
                  onClick={() => { setShowBoxFullPopup(false); onCompleteOrder(); }}
                  className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold text-base"
                  data-testid="go-to-checkout-btn"
                >
                  Complete Your Order
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <button
                  onClick={() => setShowBoxFullPopup(false)}
                  className="text-sm text-[#999] hover:text-[#666]"
                >
                  Continue browsing
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer */}
      <div className="mt-12 mb-4 max-w-lg mx-auto text-center border-t border-gray-100 pt-6" data-testid="ted-ai-disclaimer">
        <p className="text-xs text-[#999] leading-relaxed">
          Ted AI matches products by analysing your current food's protein source, ingredients, lifestage, breed size, and dietary needs against our range. Recommendations are generated using AI and are intended as suggestions only.{' '}
          <button
            onClick={() => setShowHowWeMatch(true)}
            className="text-[#0FA49C] font-semibold hover:underline"
            data-testid="how-we-match-link"
          >
            Learn how we match products
          </button>
        </p>
      </div>

      {/* How We Match Modal */}
      <AnimatePresence>
        {showHowWeMatch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowHowWeMatch(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="how-we-match-modal"
            >
              <div className="bg-[#0FA49C] p-5 text-white flex items-start justify-between sticky top-0">
                <div>
                  <h3 className="font-bold text-lg">How Ted AI Matches Products</h3>
                  <p className="text-xs text-white/80 mt-0.5">Our matching process explained</p>
                </div>
                <button onClick={() => setShowHowWeMatch(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-5 text-sm text-[#444] leading-relaxed">
                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">1. Image Analysis</h4>
                  <p>When you upload photos of your current dog food bag, Ted AI uses advanced image recognition to read the product name, brand, ingredients list, analytical constituents, and any dietary claims printed on the packaging.</p>
                </div>

                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">2. Ingredient Matching</h4>
                  <p>We compare the primary protein source (e.g. chicken, lamb, salmon), key ingredients, and nutritional profile of your current food against every product in our range. This ensures we recommend foods with similar or improved nutritional characteristics.</p>
                </div>

                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">3. Lifestage & Breed Filtering</h4>
                  <p>If your current food is formulated for a specific lifestage (puppy, adult, or senior) or breed size (small or large), Ted will only recommend products from our range that match. We never suggest an adult food for a puppy, or a large breed formula for a small dog.</p>
                </div>

                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">4. Specialist Diet Protection</h4>
                  <p>If your dog is currently on a hydrolysed protein diet, Ted will only recommend products from our specialist hydrolysed range. We understand that dogs on these diets have specific sensitivities that must be respected.</p>
                </div>

                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">5. Quality Comparison</h4>
                  <p>For each match, Ted explains why the product was selected, how it compares to your current food, and what specific benefits it offers — such as freshly prepared meat or fish, added prebiotics (MOS & FOS), superfood blends, omega-3 fatty acids, and the absence of artificial additives.</p>
                </div>

                <div>
                  <h4 className="font-bold text-[#222] text-base mb-2">6. Match Scoring</h4>
                  <p>Each recommendation receives a match score based on how closely it aligns with your current food's profile. Higher scores indicate a closer match in protein type, dietary requirements, and nutritional balance.</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-[#999]"><strong>Disclaimer:</strong> Ted AI recommendations are generated using artificial intelligence and are intended as helpful suggestions only. They do not constitute veterinary advice. If your dog has specific medical dietary requirements, please consult your veterinarian before changing their food. All Ultimate Pet Foods products are formulated by nutritionists and developed with veterinary expertise.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
